import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
  FileText,
  Folder,
  Link2,
  Plus,
  Sparkles,
  Trash2,
  UserRound,
} from 'lucide-react';
import B2BLayout from '../../components/b2b/B2BLayout';
import { PageLeaveConfirmModal, SelectList, Toast } from '../../components/ui';
import { GenerationProgressView } from '../../features/persona/components/GenerationProgressView';
import PersonaResultHeader from '../../features/persona/components/PersonaResultHeader';
import ResearchPlanImportModal from '../../features/persona/components/ResearchPlanImportModal';
import {
  PersonaAvatar as SharedPersonaAvatar,
  PersonaState,
  PersonaTagChip,
} from '../../features/persona/components/PersonaShared';
import {
  groupInterviewPersonas,
  interviewDraftToQuestionSet,
  interviewQuestionSetToDraft,
  normalizeInterview,
  personaApi,
  personaInterviewApi,
  personaResultShareApi,
  resolvePersonaApiErrorMessage,
} from '../../features/persona/services/personaService';
import { downloadExportFile, sanitizeCsvFilename } from '../../features/persona/utils/csvExport';
import { getWorkspaceArtifactContent, getWorkspaceArtifactOptions } from '../../services/api';
import {
  formatArtifactDate,
  mapPlanContentToInterviewForm,
} from '../../features/research/utils/researchArtifacts';
import sparkleIcon from '../../assets/icons/special/ico_sparkle.svg';
import './B2BAIInterviewPage.scss';

const TOTAL_STEPS = 3;
const INTERVIEW_PERSONA_SELECTION_LIMIT = 6;
const INTERVIEW_NAME_MAX_LENGTH = 60;
const INTERVIEW_GOAL_MAX_LENGTH = 300;
const INTERVIEW_PRODUCT_MAX_LENGTH = 150;
const DEFAULT_INTERVIEW_DEPTH = 'standard';
const INTERVIEW_LIST_PATH = '/tests?tab=interview';
const INTERVIEW_GENERATION_STAGE_INTERVAL_MS = 10000;
const INTERVIEW_GENERATION_NOTICE = '결과 생성까지 약 2~3분이 소요됩니다. 이 화면을 벗어나도 분석은 중단되지 않으며, 완료된 결과는 UX테스트 목록에서 확인하실 수 있습니다.';
const INTERVIEW_GENERATION_STAGES = [
  {
    label: '퍼소나 로드',
    message: '인터뷰를 진행할 퍼소나의 성향과 경험 데이터를 불러오고 있습니다.',
  },
  {
    label: '조사 목적 및 가이드라인 분석',
    message: '리서치 목적과 가이드라인을 기반으로 질문의 핵심 방향을 분석합니다.',
  },
  {
    label: '상호 작용 및 대화 전개',
    message: '흐름에 맞춰 퍼소나와 자연스러운 1:1 대화를 시뮬레이션합니다.',
  },
  {
    label: '인터뷰 결과 작성',
    message: '주고받은 대화 내용을 바탕으로 최종 인터뷰 대화 전문을 생성 중입니다.',
  },
];
const cloneValue = (value) => JSON.parse(JSON.stringify(value));
const sameId = (left, right) => String(left) === String(right);
const formatAgeLabel = (value) => {
  const text = String(value || '').trim();
  if (!text || text === '-') return '연령 미상';
  return text.endsWith('세') ? text : `${text}세`;
};
const getInterviewPersonaProfile = (persona) => ({
  gender: persona?.gender && persona.gender !== '-' ? persona.gender : '성별 미상',
  age: formatAgeLabel(persona?.age),
  job: persona?.title || persona?.role || persona?.occupation || '직업 미상',
  personality: persona?.personality || '성격 정보가 아직 등록되지 않았습니다.',
  behavior: persona?.behavior || persona?.behaviours || '행동 패턴 정보가 아직 등록되지 않았습니다.',
});

const getInterviewPersonaMetaLabel = (persona) => {
  const profile = getInterviewPersonaProfile(persona);
  return [profile.gender, profile.age, profile.job].filter(Boolean).join(' · ');
};

const getResultPersonaDisplay = (result, selectedPersonas = []) => {
  const fallbackPersona = selectedPersonas.find((persona) => sameId(persona.id, result?.personaId)) || {};
  const snapshot = result?.personaSnapshot || {};
  return {
    ...fallbackPersona,
    ...snapshot,
    id: snapshot.id || fallbackPersona.id || result?.personaId,
    name: result?.personaName || snapshot.name || fallbackPersona.name || '퍼소나',
    tag: snapshot.tag || fallbackPersona.tag || '',
    imageUrl: snapshot.imageUrl || snapshot.image_url || fallbackPersona.imageUrl || fallbackPersona.image_url,
  };
};

const DEPTH_OPTIONS = [
  { id: 'quick', title: '빠른인터뷰', description: '핵심질문 위주의 짧은 인터뷰 (약 5분)' },
  { id: 'standard', title: '표준인터뷰', description: '균형잡힌 깊이의 일반적인 인터뷰 (약 10분)' },
  { id: 'deep', title: '심층 인터뷰', description: '상세한 탐색을 위한 심층 인터뷰 (약 15분)' },
];

const DEFAULT_FORM = {
  interviewName: '',
  goal: '',
  product: '',
};

const EXPORT_OPTIONS = [
  { value: 'csv', label: 'CSV (.csv)' },
  { value: 'xlsx', label: 'Excel (.xlsx)' },
];

const INTERVIEW_EXPORT_HEADERS = [
  '퍼소나명',
  '퍼소나 정보',
  '상태',
  '질문 번호',
  '질문',
  '답변',
  '오류 메시지',
];

const INTERVIEW_META_HEADERS = ['항목', '내용'];

const INTERVIEW_QUESTION_HEADERS = [
  '질문 영역',
  '질문 번호',
  '질문',
];

const getInterviewDepthLabel = (depth) => (
  DEPTH_OPTIONS.find((item) => item.id === depth)?.title || depth || '표준인터뷰'
);

const buildInterviewQuestionRows = (interview = {}) => {
  const questionSet = interview?.questionSet || {};
  const draft = interviewQuestionSetToDraft(questionSet);
  const sections = [
    ['opening', '오프닝 질문', draft.opening],
    ...draft.taskGroups.map((group, index) => [`task_${index + 1}`, group.title || `영역${index + 1}`, group.questions]),
    ['closing', '마무리 질문', draft.closing],
    ['follow_ups', '후속질문 전략', draft.followUps],
  ];

  return sections.flatMap(([sectionKey, sectionLabel, questions]) => (
    (questions || []).map((question, index) => ({
      '질문 영역': sectionLabel || sectionKey,
      '질문 번호': index + 1,
      '질문': question,
    }))
  ));
};

const getInterviewPersonaInfoText = (persona = {}) => {
  const profile = getInterviewPersonaProfile(persona);
  return [profile.gender, profile.age, profile.job].filter(Boolean).join(' / ');
};

const buildInterviewExportData = (interview = {}) => {
  const results = interview?.results || [];
  const rows = [];
  const summary = interview?.summary || {};
  const metaRows = [
    { '항목': '인터뷰명', '내용': interview?.name || '' },
    { '항목': '인터뷰 목표', '내용': interview?.goal || '' },
    { '항목': '제품/서비스 설명', '내용': interview?.productDescription || '' },
    { '항목': '인터뷰 깊이', '내용': getInterviewDepthLabel(interview?.length || DEFAULT_INTERVIEW_DEPTH) },
    { '항목': '상태', '내용': interview?.status || '' },
    { '항목': '생성일', '내용': formatDate(interview?.createdAt) },
    { '항목': '완료일', '내용': formatDate(interview?.completedAt) },
    { '항목': '응답 퍼소나 수', '내용': summary.totalResponses || results.length || '' },
    { '항목': '실패 퍼소나 수', '내용': summary.failedResponses || '' },
  ];
  results.forEach((result) => {
    const persona = result.personaSnapshot || {};
    const personaName = result.personaName || persona.name || '';
    const personaInfo = getInterviewPersonaInfoText(persona);
    const turns = result.turns || [];
    if (!turns.length) {
      rows.push({
        '퍼소나명': personaName,
        '퍼소나 정보': personaInfo,
        '상태': result.status || '',
        '질문 번호': '',
        '질문': '',
        '답변': '',
        '오류 메시지': result.errorMessage || '',
      });
      return;
    }

    turns.forEach((turn, index) => {
      const isFirstPersonaRow = index === 0;
      rows.push({
        '퍼소나명': isFirstPersonaRow ? personaName : '',
        '퍼소나 정보': isFirstPersonaRow ? personaInfo : '',
        '상태': isFirstPersonaRow ? result.status || '' : '',
        '질문 번호': index + 1,
        '질문': turn.question || '',
        '답변': turn.answer || '',
        '오류 메시지': isFirstPersonaRow ? result.errorMessage || '' : '',
      });
    });
  });

  const questionRows = buildInterviewQuestionRows(interview);
  return {
    metaHeaders: INTERVIEW_META_HEADERS,
    metaRows,
    headers: INTERVIEW_EXPORT_HEADERS,
    rows,
    sheets: [
      { name: '요약', headers: INTERVIEW_META_HEADERS, rows: metaRows },
      { name: '퍼소나별 인터뷰', headers: INTERVIEW_EXPORT_HEADERS, rows },
      { name: '질문 목록', headers: INTERVIEW_QUESTION_HEADERS, rows: questionRows },
    ],
  };
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('/');
};

const formatSharedAt = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '공유 시각을 확인할 수 없습니다.';
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatInterviewError = (error, fallback) => resolvePersonaApiErrorMessage(error, fallback);

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

const isInterviewReadyForDisplay = (loaded) => {
  const status = String(loaded?.status || '').toLowerCase();
  if (status === 'completed' || status === 'completed_with_errors') return true;
  return status === 'running' && Array.isArray(loaded?.results) && loaded.results.length > 0;
};

const recoverInterviewAfterRunError = async (interviewId, { attempts = 5, delayMs = 2000 } = {}) => {
  for (let index = 0; index < attempts; index += 1) {
    if (index > 0) {
      await wait(delayMs);
    }
    try {
      const loaded = await personaInterviewApi.getInterview(interviewId);
      if (isInterviewReadyForDisplay(loaded)) {
        return loaded;
      }
    } catch {
      // 백엔드가 계속 처리 중일 수 있어 폴링을 이어갑니다.
    }
  }
  return null;
};

function InterviewHeader({ step, title = '1:1 AI 인터뷰', onBack, backLabel = '이전' }) {
  return (
    <header className="ai-interview-header">
      <button type="button" className="ai-interview-icon-button" onClick={onBack} aria-label={backLabel}>
        <ChevronLeft size={24} />
      </button>
      <div>
        <h1>{title}</h1>
        <p>타겟 퍼소나와의 심층 인터뷰를 진행하고 핵심 인사이트를 도출합니다.</p>
      </div>
      {step ? <ProgressStep current={step} total={TOTAL_STEPS} /> : null}
    </header>
  );
}

function ProgressStep({ current, total }) {
  return (
    <div className="ai-interview-progress" aria-label={`${current}/${total} 단계`}>
      <div className="ai-interview-progress__bar">
        {Array.from({ length: total }).map((_, index) => (
          <span key={index} className={index < current ? 'active' : ''} />
        ))}
      </div>
      <div className="ai-interview-progress__count">
        <strong>{current}</strong>
        <span />
        <em>{total}</em>
      </div>
    </div>
  );
}

function FieldLabel({ children, required = false }) {
  return (
    <label className="ai-interview-field-label">
      {children}
      {required ? <span aria-hidden="true">*</span> : null}
    </label>
  );
}

function ModalShell({ children, size = 'md' }) {
  return (
    <div className="ai-interview-modal-backdrop" role="presentation">
      <section
        className={`ai-interview-modal ai-interview-modal--${size}`}
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </section>
    </div>
  );
}

function ResultModalFrame({ title, description, children, footer }) {
  return (
    <div className="ai-interview-result-modal-backdrop" role="presentation">
      <section
        className="ai-interview-result-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-interview-result-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <h2 id="ai-interview-result-modal-title">{title}</h2>
          {description ? <p>{description}</p> : null}
        </header>
        <div className="ai-interview-result-modal__body">{children}</div>
        <footer>{footer}</footer>
      </section>
    </div>
  );
}

function DeleteInterviewModal({ deleting, onCancel, onConfirm }) {
  return (
    <div className="persona-modal-backdrop" role="presentation">
      <section
        className="persona-delete-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-interview-delete-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <h2 id="ai-interview-delete-modal-title">선택한 테스트를 삭제하시겠습니까?</h2>
        </header>
        <div className="persona-delete-modal__body">
          <p>삭제 시 관련 해당 테스트의 정보는 모두 삭제되며</p>
          <p>이 작업은 취소할 수 없습니다.</p>
        </div>
        <footer>
          <button type="button" onClick={onCancel} disabled={deleting}>
            취소하기
          </button>
          <button type="button" onClick={onConfirm} disabled={deleting}>
            {deleting ? '삭제 중' : '삭제하기'}
          </button>
        </footer>
      </section>
    </div>
  );
}

function SelectionDot({ selected }) {
  return <span className={`ai-interview-selection-dot ${selected ? 'selected' : ''}`} aria-hidden="true" />;
}

function PersonaAvatar({ name, imageUrl, small = false }) {
  const [hasImageError, setHasImageError] = useState(false);
  const resolvedImageUrl = String(imageUrl || '').trim();
  const initial = String(name || '퍼소나').slice(0, 1);

  useEffect(() => {
    setHasImageError(false);
  }, [resolvedImageUrl]);

  if (resolvedImageUrl && !hasImageError) {
    return (
      <img
        className={`ai-interview-avatar ${small ? 'small' : ''}`}
        src={resolvedImageUrl}
        alt={name || '퍼소나'}
        onError={() => setHasImageError(true)}
      />
    );
  }

  return <span className={`ai-interview-avatar ${small ? 'small' : ''}`}>{initial}</span>;
}

function LoadingOverlay({ message }) {
  return (
    <div className="ai-interview-loading" role="status" aria-live="polite">
      <span className="ai-interview-spinner" />
      <p>{message}</p>
    </div>
  );
}

function InterviewGenerationProgress({ intervalMs = INTERVIEW_GENERATION_STAGE_INTERVAL_MS }) {
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
    }, 500);

    return () => window.clearInterval(timer);
  }, []);

  const activeStep = Math.min(
    INTERVIEW_GENERATION_STAGES.length - 1,
    Math.floor(elapsedMs / intervalMs),
  );

  return (
    <GenerationProgressView
      stages={INTERVIEW_GENERATION_STAGES}
      currentStep={activeStep}
      ariaLabel="인터뷰 생성 진행 상태"
      notice={INTERVIEW_GENERATION_NOTICE}
    />
  );
}

function ErrorNotice({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="ai-interview-alert" role="alert">
      <p>{message}</p>
      <button type="button" onClick={onClose}>닫기</button>
    </div>
  );
}

function ResearchArtifactImportModal({
  title,
  description,
  items,
  loading,
  emptyMessage,
  selectedId,
  onSelect,
  onCancel,
  onLoad,
  loadLabel = '불러오기',
}) {
  const visibleItemCount = Math.min(Math.max(items.length, 1), 3);

  return (
    <ModalShell size="artifact" onClose={onCancel}>
      <div className="ai-interview-modal__header align-left">
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      <div className="ai-interview-modal__body ai-interview-artifact-select-body">
        <div
          className={`ai-interview-artifact-select-area is-${visibleItemCount}`}
        >
          {loading ? (
            <p className="ai-interview-dialog-copy">목록을 불러오는 중...</p>
          ) : items.length === 0 ? (
            <p className="ai-interview-dialog-copy">{emptyMessage}</p>
          ) : (
            <div className="ai-interview-select-list">
            {items.map((item) => {
              const selected = String(selectedId) === String(item.id);
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`ai-interview-select-option ${selected ? 'selected' : ''}`}
                  onClick={() => onSelect(item.id)}
                >
                  <SelectionDot selected={selected} />
                  <span>
                    <strong>{item.title}</strong>
                    <em>
                      {item.projectName ? `${item.projectName} · ` : ''}
                      생성일: {formatArtifactDate(item.createdAt) || '-'}
                    </em>
                  </span>
                </button>
              );
            })}
            </div>
          )}
        </div>
      </div>
      <footer className="ai-interview-modal__footer">
        <button type="button" className="ai-interview-button secondary" onClick={onCancel}>
          취소하기
        </button>
        <button
          type="button"
          className="ai-interview-button primary"
          onClick={onLoad}
          disabled={loading || !selectedId || items.length === 0}
        >
          {loadLabel}
        </button>
      </footer>
    </ModalShell>
  );
}

function QuestionDraftModal({ selectedDepth, disabled, onSelect, onCancel, onGenerate }) {
  return (
    <ModalShell onClose={onCancel}>
      <div className="ai-interview-modal__header centered">
        <h2>
          <Sparkles size={24} />
          AI로 인터뷰 질문 초안 생성하기
        </h2>
      </div>
      <div className="ai-interview-modal__body">
        <div className="ai-interview-select-list">
          {DEPTH_OPTIONS.map((depth) => {
            const selected = selectedDepth === depth.id;
            return (
              <SelectList
                key={depth.id}
                type="radio"
                title={depth.title}
                description={depth.description}
                selected={selected}
                width="100%"
                onClick={() => onSelect(depth.id)}
              />
            );
          })}
        </div>
      </div>
      <footer className="ai-interview-modal__footer">
        <button type="button" className="ai-interview-button secondary" onClick={onCancel}>
          취소하기
        </button>
        <button type="button" className="ai-interview-button primary" onClick={onGenerate} disabled={disabled}>
          생성하기
        </button>
      </footer>
    </ModalShell>
  );
}

function RegenerateModal({ onCancel, onConfirm }) {
  return (
    <ModalShell size="sm" onClose={onCancel}>
      <div className="ai-interview-modal__header centered compact">
        <h2>질문을 다시 생성하시겠습니까?</h2>
      </div>
      <div className="ai-interview-modal__body compact">
        <p className="ai-interview-dialog-copy">
          다시 생성할 경우, 현재 수정 중인 질문 리스트는 모두 삭제되며 복구할 수 없습니다.
          계속하시겠습니까?
        </p>
      </div>
      <footer className="ai-interview-modal__footer">
        <button type="button" className="ai-interview-button subtle" onClick={onCancel}>
          취소하기
        </button>
        <button type="button" className="ai-interview-button primary" onClick={onConfirm}>
          다시 생성하기
        </button>
      </footer>
    </ModalShell>
  );
}

function GoalStep({
  form,
  draft,
  depth,
  canContinue,
  onFormChange,
  onOpenPlans,
  onOpenGuidelines,
  onOpenDraftModal,
  onOpenRegenerate,
  onDraftChange,
  onAddDraftItem,
  onRemoveDraftItem,
  onAddTaskGroup,
  onRemoveTaskGroup,
  onTaskTitleChange,
  onNext,
}) {
  const hasDraft = Boolean(draft);

  return (
    <>
      <main className="ai-interview-body">
        <section className="ai-interview-content">
          <div className="ai-interview-section-head">
            <div>
              <h2>인터뷰 목표</h2>
              <p>인터뷰를 통해 확인하고 싶은 목표와 서비스의 핵심 내용을 알려주세요.</p>
            </div>
            <button type="button" className="ai-interview-small-button" onClick={onOpenPlans}>
              리서치 계획서 불러오기
              <FileText size={20} />
            </button>
          </div>

          <div className="ai-interview-form-field">
            <FieldLabel required>인터뷰 이름</FieldLabel>
            <input
              value={form.interviewName}
              onChange={(event) => onFormChange('interviewName', event.target.value)}
              maxLength={INTERVIEW_NAME_MAX_LENGTH}
              placeholder="인터뷰 이름을 입력해주세요. (예: 5G 무제한 요금제 가입 고객 대상 만족도 조사)"
            />
          </div>

          <div className="ai-interview-form-field">
            <FieldLabel required>인터뷰 목표</FieldLabel>
            <textarea
              value={form.goal}
              onChange={(event) => onFormChange('goal', event.target.value)}
              maxLength={INTERVIEW_GOAL_MAX_LENGTH}
              placeholder="인터뷰 목표를 입력해주세요. (예: 현재 요금제의 데이터 공유 및 멤버십 혜택이 고객의 실제 라이프스타일에 부합하는지 확인하고, 추가로 희망하는 부가 서비스 니즈를 파악하고자 함)"
            />
          </div>

          <div className="ai-interview-form-field">
            <FieldLabel>서비스/제품 설명</FieldLabel>
            <textarea
              value={form.product}
              onChange={(event) => onFormChange('product', event.target.value)}
              maxLength={INTERVIEW_PRODUCT_MAX_LENGTH}
              placeholder="인터뷰할 서비스나 제품에 대해 설명해주세요. 퍼소나가 이 내용을 바탕으로 설문에 참여합니다. (예: 유저의 현재 데이터 사용 패턴을 분석해 가장 유리한 요금제를 제공하는 5G 맞춤형 신규 통신 상품 서비스)"
            />
          </div>

          <div className="ai-interview-question-area">
            <div className="ai-interview-question-area__top">
              <FieldLabel>인터뷰 질문</FieldLabel>
              <div className="ai-interview-question-area__actions">
                <button type="button" className="ai-interview-small-button" onClick={onOpenGuidelines}>
                  가이드라인에서 질문 불러오기
                  <FileText size={18} />
                </button>
                {hasDraft ? (
                  <button type="button" className="ai-interview-small-button" onClick={onOpenRegenerate}>
                    AI로 인터뷰 질문 다시 생성하기
                    <Sparkles size={18} />
                  </button>
                ) : null}
              </div>
            </div>
            {!hasDraft ? (
              <div className="ai-interview-question-empty">
                <p>
                  입력한 정보를 바탕으로 AI가 인터뷰 질문을 생성하거나,
                  <br />
                  조사 가이드라인에서 질문을 불러온 뒤 자유롭게 수정할 수 있습니다.
                </p>
                <button type="button" className="ai-interview-small-button" onClick={onOpenDraftModal}>
                  <img src={sparkleIcon} alt="" aria-hidden="true" width="20" height="20" />
                  AI로 인터뷰 질문 초안 생성하기
                </button>
              </div>
            ) : (
              <div className="ai-interview-draft-stack">
                <QuestionListEditor
                  title="오프닝 질문"
                  prefix="질문"
                  items={draft.opening}
                  onChange={(index, value) => onDraftChange('opening', index, value)}
                  onAdd={() => onAddDraftItem('opening')}
                  onRemove={(index) => onRemoveDraftItem('opening', index)}
                />
                <TaskQuestionEditor
                  groups={draft.taskGroups}
                  onAddGroup={onAddTaskGroup}
                  onRemoveGroup={onRemoveTaskGroup}
                  onTitleChange={onTaskTitleChange}
                  onChange={(groupIndex, questionIndex, value) => onDraftChange('taskGroups', questionIndex, value, groupIndex)}
                  onAddQuestion={(groupIndex) => onAddDraftItem('taskGroups', groupIndex)}
                  onRemoveQuestion={(groupIndex, questionIndex) => onRemoveDraftItem('taskGroups', questionIndex, groupIndex)}
                />
                <QuestionListEditor
                  title="마무리 질문"
                  prefix="질문"
                  items={draft.closing}
                  onChange={(index, value) => onDraftChange('closing', index, value)}
                  onAdd={() => onAddDraftItem('closing')}
                  onRemove={(index) => onRemoveDraftItem('closing', index)}
                />
                <QuestionListEditor
                  title="후속질문 전략"
                  prefix="전략"
                  items={draft.followUps}
                  onChange={(index, value) => onDraftChange('followUps', index, value)}
                  onAdd={() => onAddDraftItem('followUps')}
                  onRemove={(index) => onRemoveDraftItem('followUps', index)}
                />
              </div>
            )}
          </div>
        </section>
      </main>
      <FlowFooter
        rightLabel="다음"
        rightDisabled={!canContinue}
        onRight={onNext}
      />
    </>
  );
}

function QuestionListEditor({ title, prefix, items, onChange, onAdd, onRemove }) {
  return (
    <section className="ai-interview-editor-box">
      <h3>{title}</h3>
      <div className="ai-interview-editor-list">
        {items.map((item, index) => (
          <div key={`${title}-${index}`} className="ai-interview-editor-row">
            <span>{prefix} {index + 1}.</span>
            <input value={item} onChange={(event) => onChange(index, event.target.value)} />
            <button type="button" onClick={() => onRemove(index)} aria-label={`${prefix} ${index + 1} 삭제`}>
              <Trash2 size={18} />
            </button>
          </div>
        ))}
        <button type="button" className="ai-interview-add-button" onClick={onAdd}>
          <Plus size={18} />
          {prefix} 추가
        </button>
      </div>
    </section>
  );
}

function TaskQuestionEditor({ groups, onAddGroup, onRemoveGroup, onTitleChange, onChange, onAddQuestion, onRemoveQuestion }) {
  return (
    <section className="ai-interview-editor-box">
      <div className="ai-interview-editor-box__head">
        <h3>핵심 과업 질문</h3>
        <button type="button" className="ai-interview-add-button bordered" onClick={onAddGroup}>
          <Plus size={18} />
          영역 추가
        </button>
      </div>
      <div className="ai-interview-task-groups">
        {groups.map((group, groupIndex) => (
          <div key={group.id} className="ai-interview-task-group">
            <div className="ai-interview-task-group__head">
              <input value={group.title} onChange={(event) => onTitleChange(groupIndex, event.target.value)} aria-label={`영역 ${groupIndex + 1} 이름`} />
              <button type="button" onClick={() => onRemoveGroup(groupIndex)}>
                <Trash2 size={18} />
                영역삭제
              </button>
            </div>
            {group.questions.map((question, questionIndex) => (
              <div key={`${group.id}-${questionIndex}`} className="ai-interview-editor-row">
                <span>질문 {questionIndex + 1}.</span>
                <input value={question} onChange={(event) => onChange(groupIndex, questionIndex, event.target.value)} />
                <button type="button" onClick={() => onRemoveQuestion(groupIndex, questionIndex)} aria-label={`질문 ${questionIndex + 1} 삭제`}>
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
            <button type="button" className="ai-interview-add-button" onClick={() => onAddQuestion(groupIndex)}>
              <Plus size={18} />
              질문 추가
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function PersonaStep({ groups, loading, selectedPersonaIds, onToggle, onPrevious, onNext }) {
  const [collapsedGroupIds, setCollapsedGroupIds] = useState([]);
  const collapseInitializedRef = useRef(false);
  const allPersonas = groups.flatMap((group) => group.personas);
  useEffect(() => {
    if (collapseInitializedRef.current || groups.length === 0) return;
    setCollapsedGroupIds(groups.map((group) => group.id));
    collapseInitializedRef.current = true;
  }, [groups]);
  const toggleGroup = (groupId) => {
    setCollapsedGroupIds((current) => (
      current.some((id) => sameId(id, groupId))
        ? current.filter((id) => !sameId(id, groupId))
        : [...current, groupId]
    ));
  };

  return (
    <>
      <main className="ai-interview-body">
        <section className="ai-interview-content">
          <div className="ai-interview-section-head">
            <div>
              <h2>퍼소나 선택</h2>
              <p>테스트에 참여할 퍼소나를 선택해주세요. (최대 {INTERVIEW_PERSONA_SELECTION_LIMIT}명 선택 가능)</p>
            </div>
          </div>
          {loading ? (
            <div className="ai-interview-empty-panel">퍼소나 목록을 불러오고 있습니다.</div>
          ) : allPersonas.length === 0 ? (
            <div className="ai-interview-empty-panel">인터뷰에 사용할 수 있는 퍼소나가 없습니다.</div>
          ) : (
            <>
              <div className="ai-interview-persona-groups">
                {groups.map((group) => {
                  const groupSelected = group.personas.filter((persona) => selectedPersonaIds.some((id) => sameId(id, persona.id))).length;
                  const collapsed = collapsedGroupIds.some((id) => sameId(id, group.id));
                  const gridId = `ai-interview-persona-group-${group.id}`;
                  return (
                    <section key={group.id} className={`ai-interview-persona-group ${collapsed ? 'is-collapsed' : ''}`}>
                      <header>
                        <div>
                          <Folder size={30} fill="#1f2023" />
                          <span>
                            <strong>{group.name}</strong>
                            <em>총 퍼소나 {group.personas.length}</em>
                          </span>
                        </div>
                        <p>{groupSelected}명 선택</p>
                        <button
                          type="button"
                          className="ai-interview-persona-toggle"
                          onClick={() => toggleGroup(group.id)}
                          aria-expanded={!collapsed}
                          aria-controls={gridId}
                          aria-label={`${group.name} 퍼소나 ${collapsed ? '펼치기' : '접기'}`}
                        >
                          <ChevronDown size={24} />
                        </button>
                      </header>
                      {group.personas.length > 0 && !collapsed ? (
                        <div className="ai-interview-persona-grid" id={gridId}>
                          {group.personas.map((persona) => {
                            const selected = selectedPersonaIds.some((id) => sameId(id, persona.id));
                            const disabled = !selected && selectedPersonaIds.length >= INTERVIEW_PERSONA_SELECTION_LIMIT;
                            return (
                              <InterviewPersonaCard
                                key={persona.id}
                                persona={persona}
                                selected={selected}
                                disabled={disabled}
                                onToggle={onToggle}
                              />
                            );
                          })}
                        </div>
                      ) : null}
                    </section>
                  );
                })}
              </div>
            </>
          )}
        </section>
      </main>
      <FlowFooter
        leftLabel="이전"
        onLeft={onPrevious}
        rightLabel="다음"
        rightDisabled={selectedPersonaIds.length === 0}
        onRight={onNext}
      />
    </>
  );
}

function InterviewPersonaCard({ persona, selected, disabled, onToggle }) {
  const profile = getInterviewPersonaProfile(persona);
  const metaLabel = [profile.gender, profile.age, profile.job].filter(Boolean).join(' · ');
  return (
    <article className={`ai-interview-persona-card${selected ? ' selected' : ''}${disabled ? ' disabled' : ''}`}>
      <span
        className="ai-interview-persona-card__check"
        role="checkbox"
        aria-checked={selected}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={() => {
          if (!disabled) onToggle(persona.id);
        }}
        onKeyDown={(event) => {
          if (disabled || (event.key !== 'Enter' && event.key !== ' ')) return;
          event.preventDefault();
          onToggle(persona.id);
        }}
      >
        <input type="checkbox" checked={selected} disabled={disabled} readOnly tabIndex={-1} />
        <span />
      </span>
      <button
        type="button"
        className="ai-interview-persona-card__content"
        onClick={() => onToggle(persona.id)}
        aria-pressed={selected}
        disabled={disabled}
      >
        <div className="ai-interview-persona-card__top">
          <SharedPersonaAvatar persona={persona} size="sm" />
          <div className="ai-interview-persona-card__identity">
            <div className="ai-interview-persona-card__identity-main">
              <strong>{persona?.name || '이름 없는 퍼소나'}</strong>
              <PersonaTagChip tag={persona?.tag} />
            </div>
            <p className="ai-interview-persona-card__meta">{metaLabel}</p>
          </div>
        </div>
        <div className="ai-interview-persona-card__divider" />
        <section className="ai-interview-persona-card__section">
          <strong>성격</strong>
          <p>{profile.personality}</p>
        </section>
        <section className="ai-interview-persona-card__section">
          <strong>행동 패턴</strong>
          <p>{profile.behavior}</p>
        </section>
      </button>
    </article>
  );
}

function PersonaSummary({ persona, compact }) {
  const name = persona?.name || '퍼소나';
  const metaLabel = getInterviewPersonaMetaLabel(persona);
  return (
    <div className={`ai-interview-persona-summary ${compact ? 'compact' : ''}`}>
      <div className="ai-interview-persona-summary__head">
        <PersonaAvatar name={name} imageUrl={persona?.imageUrl || persona?.image_url} />
        <div className="ai-interview-persona-summary__identity">
          <div className="ai-interview-persona-summary__identity-main">
            <strong>{name}</strong>
            <PersonaTagChip tag={persona?.tag} />
          </div>
          <p>{metaLabel}</p>
        </div>
      </div>
    </div>
  );
}

function ReviewStep({ form, draft, depth, selectedPersonas, expanded, onToggleExpanded, onPrevious, onCreate }) {
  const depthLabel = DEPTH_OPTIONS.find((item) => item.id === depth)?.title || '-';

  return (
    <>
      <main className="ai-interview-body">
        <section className="ai-interview-content">
          <div className="ai-interview-section-head">
            <div>
              <h2>인터뷰 정보 확인</h2>
              <p>입력하신 정보와 AI가 생성한 인터뷰 질문을 확인해주세요. 내용이 정확하다면 테스트를 생성합니다.</p>
            </div>
          </div>
          <div className="ai-interview-review-stack">
            <ReviewCard step="Step.01" title="인터뷰 목표 선택">
              <ReviewRow label="인터뷰 이름" value={form.interviewName || '-'} />
              <ReviewRow label="인터뷰 목표" value={form.goal || '-'} />
              <ReviewRow label="서비스/제품 설명" value={form.product || '-'} />
              <ReviewRow label="인터뷰 깊이" value={depthLabel} />
              <div className="ai-interview-review-row">
                <strong>인터뷰 질문</strong>
                <button type="button" className="ai-interview-view-toggle" onClick={onToggleExpanded}>
                  항목 보기
                  {expanded ? <ChevronDown className="rotate" size={18} /> : <ChevronDown size={18} />}
                </button>
              </div>
              {expanded ? <ReviewQuestions draft={draft} /> : null}
            </ReviewCard>
            <ReviewCard step="Step.02" title="퍼소나 선택">
              <ReviewRow label="퍼소나 수" value={`${selectedPersonas.length}명`} />
            </ReviewCard>
          </div>
        </section>
      </main>
      <FlowFooter
        leftLabel="이전"
        onLeft={onPrevious}
        rightLabel="생성하기"
        onRight={onCreate}
      />
    </>
  );
}

function ReviewCard({ step, title, children }) {
  return (
    <section className="ai-interview-review-card">
      <header>
        <span>{step}</span>
        <h3>{title}</h3>
      </header>
      {children}
    </section>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="ai-interview-review-row">
      <strong>{label}</strong>
      <p>{value}</p>
    </div>
  );
}

function ReviewQuestions({ draft }) {
  if (!draft) return null;
  const sections = [
    ['오프닝 질문', draft.opening],
    ...draft.taskGroups.map((group) => [group.title, group.questions]),
    ['마무리 질문', draft.closing],
    ['후속질문 전략', draft.followUps],
  ];

  return (
    <div className="ai-interview-review-questions">
      {sections.map(([title, items]) => (
        <section key={title}>
          <h4>{title}</h4>
          <ul>
            {items.map((item, index) => (
              <li key={`${title}-${index}`}>{item}</li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function ResultView({
  interview,
  form,
  depth,
  selectedPersonas,
  activePersonaId,
  onActivePersonaChange,
  onBack,
  onOpenShare,
  onOpenDelete,
  onOpenExport,
  onOpenInsights,
  shareMode = false,
}) {
  const results = interview?.results || [];
  const activeResult = results.find((result) => sameId(result.personaId, activePersonaId)) || results[0] || null;
  const activePersona = activeResult
    ? getResultPersonaDisplay(activeResult, selectedPersonas)
    : selectedPersonas[0];
  const depthLabel = DEPTH_OPTIONS.find((item) => item.id === (interview?.length || depth))?.title || '표준인터뷰';
  const summary = interview?.summary || {};
  const createdAt = formatDate(interview?.createdAt);
  const hasInsightEvidence = results.some((row) => row.status !== 'failed' && (
    (Array.isArray(row.turns) && row.turns.length > 0)
    || Object.keys(row.summary || {}).length > 0
  ));
  const canOpenInsights = !shareMode && hasInsightEvidence;

  return (
    <>
      {!shareMode ? (
        <PersonaResultHeader
          title="1:1 AI 인터뷰 결과"
          description="타겟 퍼소나와의 심층 인터뷰를 진행하고 핵심 인사이트를 도출합니다."
          onBack={onBack}
        />
      ) : null}
      <main className="ai-interview-result-body">
        <section className="ai-interview-result-content">
          <div className="ai-interview-result-head">
            <div className="ai-interview-result-title">
              <h2>{interview?.name || form.interviewName || '1:1 AI 인터뷰'}</h2>
            </div>
            {!shareMode ? (
              <div className="ai-interview-result-actions">
                <button type="button" onClick={onOpenShare}><Link2 size={18} />공유</button>
                <button type="button" onClick={onOpenExport}><Download size={18} />내보내기</button>
                <button type="button" onClick={onOpenDelete}><Trash2 size={18} />삭제</button>
              </div>
            ) : null}
            <div className="ai-interview-result-meta">
              <span>심층인터뷰</span>
              <ChevronRight size={16} />
              <span>{depthLabel}</span>
              <i />
              <em><UserRound size={16} />{summary.totalResponses || results.length || selectedPersonas.length}명</em>
              <i />
              <em>{createdAt}</em>
              {interview?.status === 'completed_with_errors' ? (
                <>
                  <i />
                  <em>일부 실패 {summary.failedResponses || 0}명</em>
                </>
              ) : null}
            </div>
            <button
              type="button"
              className="ui-result-insight-report-btn ai-interview-insight-btn"
              onClick={onOpenInsights}
              disabled={!canOpenInsights}
              title={canOpenInsights ? undefined : '완료된 인터뷰 결과가 필요합니다.'}
            >
              <Sparkles size={16} />
                인사이트 보고서 생성
            </button>
            <p className="ai-interview-result-goal">{interview?.goal || form.goal || '-'}</p>
          </div>

          <div
            className={[
              'ai-interview-persona-tabs',
              results.length === 4 && 'ai-interview-persona-tabs--quad',
              results.length >= 5 && 'ai-interview-persona-tabs--triple',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {results.map((result) => {
              const personaName = result.personaName || result.personaSnapshot?.name || '퍼소나';
              return (
                <button
                  key={result.id || result.personaId}
                  type="button"
                  className={sameId(activeResult?.personaId, result.personaId) ? 'selected' : ''}
                  onClick={() => onActivePersonaChange(result.personaId)}
                >
                  <PersonaAvatar name={personaName} imageUrl={result.personaSnapshot?.imageUrl} small />
                  {personaName}
                  {result.status === 'failed' ? <em className="ai-interview-failed-badge">실패</em> : null}
                </button>
              );
            })}
          </div>

          {activePersona ? (
            <section className="ai-interview-transcript">
              <PersonaSummary persona={activePersona} compact />
              {activeResult?.status === 'failed' ? (
                <div className="ai-interview-result-error">
                  {activeResult.errorMessage || '이 퍼소나 인터뷰 실행에 실패했습니다.'}
                </div>
              ) : (
                <>
                  <div className="ai-interview-dialog-list">
                    {(activeResult?.turns || []).map((item, index) => (
                      <React.Fragment key={`${activeResult?.id || activeResult?.personaId}-${index}`}>
                        <div className="ai-interview-message ai">
                          <p>{item.question}</p>
                        </div>
                        <div className="ai-interview-message persona">
                          <p>{item.answer}</p>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </>
              )}
            </section>
          ) : (
            <section className="ai-interview-transcript">
              <div className="ai-interview-empty-panel">표시할 인터뷰 결과가 없습니다.</div>
            </section>
          )}
        </section>
      </main>
    </>
  );
}

function FlowFooter({ leftLabel, rightLabel, rightDisabled = false, onLeft, onRight }) {
  return (
    <footer className="ai-interview-footer">
      <div>
        {leftLabel ? (
          <button type="button" className="ai-interview-flow-button secondary" onClick={onLeft}>
            <ChevronLeft size={24} />
            {leftLabel}
          </button>
        ) : null}
        <button
          type="button"
          className="ai-interview-flow-button primary"
          disabled={rightDisabled}
          onClick={onRight}
        >
          {rightLabel}
          {rightLabel === '다음' ? <ChevronRight size={24} /> : null}
        </button>
      </div>
    </footer>
  );
}

function AIInterviewPageShell({ children, isShareView, navigationGuard }) {
  if (isShareView) {
    return <>{children}</>;
  }

  return (
    <B2BLayout
      hideCollapseButton={false}
      defaultCollapsed={true}
      hidePageHeader={true}
      navigationGuard={navigationGuard}
    >
      {children}
    </B2BLayout>
  );
}

function B2BAIInterviewPage({ shareMode = false }) {
  const { studySlug, interviewId, shareToken } = useParams();
  const isShareView = shareMode || Boolean(shareToken);
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [draft, setDraft] = useState(null);
  const [depth, setDepth] = useState(DEFAULT_INTERVIEW_DEPTH);
  const [personaGroups, setPersonaGroups] = useState([]);
  const [personasLoading, setPersonasLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedGuidelineId, setSelectedGuidelineId] = useState(null);
  const [artifactsLoading, setArtifactsLoading] = useState(false);
  const [selectedPersonaIds, setSelectedPersonaIds] = useState([]);
  const [activePersonaId, setActivePersonaId] = useState(null);
  const [interview, setInterview] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [reviewExpanded, setReviewExpanded] = useState(false);
  const [modal, setModal] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [creatingInterview, setCreatingInterview] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [pendingExit, setPendingExit] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'success' });
  const [exportFormat, setExportFormat] = useState('csv');
  const [planOptions, setPlanOptions] = useState([]);
  const [guidelineOptions, setGuidelineOptions] = useState([]);
  const loadedArtifactTypesRef = useRef(new Set());

  const ensureResearchArtifacts = useCallback(async (artifactType) => {
    if (loadedArtifactTypesRef.current.has(artifactType)) return;
    setArtifactsLoading(true);
    setErrorMessage('');
    try {
      const response = await getWorkspaceArtifactOptions({ artifactTypes: [artifactType] });
      const items = response.items || [];
      if (artifactType === 'plan') setPlanOptions(items);
      if (artifactType === 'guideline') setGuidelineOptions(items);
      loadedArtifactTypesRef.current.add(artifactType);
    } catch (error) {
      setErrorMessage(error.message || '리서치 자료를 불러오지 못했습니다.');
    } finally {
      setArtifactsLoading(false);
    }
  }, []);

  const allPersonas = useMemo(() => personaGroups.flatMap((group) => group.personas), [personaGroups]);
  const selectedPersonas = useMemo(
    () => allPersonas.filter((persona) => selectedPersonaIds.some((id) => sameId(id, persona.id))),
    [allPersonas, selectedPersonaIds]
  );
  const canContinueGoal = Boolean(form.interviewName.trim() && form.goal.trim() && draft);
  const hasDraftChanges = useMemo(() => (
    Boolean(
      form.interviewName.trim()
      || form.goal.trim()
      || form.product.trim()
      || draft
      || selectedPersonaIds.length > 0
      || step > 1
    )
  ), [draft, form, selectedPersonaIds.length, step]);
  const shareUrl = shareLink || (typeof window !== 'undefined' ? window.location.href : '');

  useEffect(() => {
    if (isShareView) {
      setPersonasLoading(false);
      return undefined;
    }
    let active = true;
    const loadPersonas = async () => {
      setPersonasLoading(true);
      try {
        const [personas, folders] = await Promise.all([
          personaInterviewApi.listPersonas(),
          personaApi.listFolders().catch(() => []),
        ]);
        if (active) setPersonaGroups(groupInterviewPersonas(personas, folders));
      } catch (error) {
        if (active) {
          setPersonaGroups([]);
          setErrorMessage(formatInterviewError(error, '퍼소나 목록을 불러오지 못했습니다.'));
        }
      } finally {
        if (active) setPersonasLoading(false);
      }
    };
    loadPersonas();
    return () => {
      active = false;
    };
  }, [isShareView]);

  useEffect(() => {
    if (!interviewId && !isShareView) return undefined;
    let active = true;
    const loadInterview = async () => {
      setLoadingMessage('인터뷰 결과를 불러오고 있습니다.');
      setErrorMessage('');
      try {
        const loaded = isShareView
          ? await personaResultShareApi.getShare(shareToken).then((shared) => {
              if (shared?.resourceType !== 'interview') throw new Error('공유 링크를 찾을 수 없습니다.');
              return normalizeInterview(shared.result || {});
            })
          : await personaInterviewApi.getInterview(interviewId);
        if (!active) return;
        setInterview(loaded);
        setForm({
          interviewName: loaded?.name || '',
          goal: loaded?.goal || '',
          product: loaded?.productDescription || '',
        });
        setDepth(loaded?.length || DEFAULT_INTERVIEW_DEPTH);
        setDraft(interviewQuestionSetToDraft(loaded?.questionSet || {}));
        setSelectedPersonaIds(loaded?.personaIds || []);
        setActivePersonaId(loaded?.results?.[0]?.personaId || loaded?.personaIds?.[0] || null);
        setShowResult(true);
      } catch (error) {
        if (active) {
          setErrorMessage(isShareView ? '공유 링크를 찾을 수 없습니다.' : formatInterviewError(error, '인터뷰 결과를 불러오지 못했습니다.'));
        }
      } finally {
        if (active) setLoadingMessage('');
      }
    };
    loadInterview();
    return () => {
      active = false;
    };
  }, [interviewId, isShareView, shareToken]);

  const getBackTarget = () => (studySlug ? `/b2b/study/${studySlug}` : INTERVIEW_LIST_PATH);

  const requestExit = useCallback((action) => {
    if (showResult || !hasDraftChanges) return true;
    setPendingExit(action);
    return false;
  }, [hasDraftChanges, showResult]);

  const handleNavigationGuard = useCallback((targetPath = INTERVIEW_LIST_PATH, proceed) => (
    requestExit({ type: 'navigate', targetPath, proceed })
  ), [requestExit]);

  const handleBack = () => {
    if (showResult) {
      navigate(getBackTarget());
      return;
    }
    if (requestExit({ type: 'navigate', targetPath: getBackTarget() })) {
      navigate(getBackTarget());
    }
  };

  const requestPreviousStep = (targetStep) => {
    if (requestExit({ type: 'step', targetStep })) {
      setStep(targetStep);
    }
  };

  const handleCancelPendingExit = () => {
    setPendingExit(null);
  };

  const handleConfirmPendingExit = () => {
    const action = pendingExit;
    setPendingExit(null);
    if (!action) return;
    if (action.type === 'step') {
      setStep(action.targetStep);
      return;
    }
    if (typeof action.proceed === 'function') {
      action.proceed();
      return;
    }
    navigate(action.targetPath || INTERVIEW_LIST_PATH);
  };

  const updateForm = (field, value) => {
    const limits = {
      interviewName: INTERVIEW_NAME_MAX_LENGTH,
      goal: INTERVIEW_GOAL_MAX_LENGTH,
      product: INTERVIEW_PRODUCT_MAX_LENGTH,
    };
    const limit = limits[field];
    const nextValue = limit ? String(value).slice(0, limit) : value;
    setForm((current) => ({ ...current, [field]: nextValue }));
  };

  const resolveArtifactContent = async (item) => {
    if (item?.content) return item.content;
    if (!item?.studyId || !item?.id) return '';
    const response = await getWorkspaceArtifactContent(item.id);
    return response.item?.content || '';
  };

  const handleLoadPlan = async () => {
    const selectedPlan = planOptions.find((plan) => String(plan.id) === String(selectedPlanId));
    if (!selectedPlan) return;
    try {
      setLoadingMessage('계획서를 불러오고 있습니다.');
      const content = await resolveArtifactContent(selectedPlan);
      if (!content) {
        setErrorMessage('계획서 원문을 불러오지 못했습니다. 다시 시도해 주세요.');
        return;
      }
      const generatedContext = await personaInterviewApi.generateContextFromPlan({
        planContent: content,
        studyName: selectedPlan.studyName || selectedPlan.title,
      });
      const fallback = mapPlanContentToInterviewForm(
        { ...selectedPlan, content },
        {
          interviewName: INTERVIEW_NAME_MAX_LENGTH,
          goal: INTERVIEW_GOAL_MAX_LENGTH,
          product: INTERVIEW_PRODUCT_MAX_LENGTH,
        }
      );
      setForm({
        interviewName: String(generatedContext?.interviewName || fallback.interviewName)
          .slice(0, INTERVIEW_NAME_MAX_LENGTH),
        goal: String(generatedContext?.goal || fallback.goal).slice(0, INTERVIEW_GOAL_MAX_LENGTH),
        product: String(generatedContext?.productDescription || fallback.product)
          .slice(0, INTERVIEW_PRODUCT_MAX_LENGTH),
      });
      setModal(null);
    } catch (error) {
      setErrorMessage(formatInterviewError(error, '계획서를 불러오지 못했습니다.'));
    } finally {
      setLoadingMessage('');
    }
  };

  const handleLoadGuideline = async () => {
    const selectedGuideline = guidelineOptions.find(
      (item) => String(item.id) === String(selectedGuidelineId)
    );
    if (!selectedGuideline) return;
    try {
      setLoadingMessage('가이드라인을 인터뷰 질문으로 구성하고 있습니다.');
      const content = await resolveArtifactContent(selectedGuideline);
      if (!content) {
        setErrorMessage('가이드라인 원문을 불러오지 못했습니다. 다시 시도해 주세요.');
        return;
      }
      const questionSet = await personaInterviewApi.generateQuestionsFromGuideline({
        guidelineContent: content,
      });
      setDraft(interviewQuestionSetToDraft(questionSet));
      setModal(null);
    } catch (error) {
      setErrorMessage(formatInterviewError(error, '가이드라인을 불러오지 못했습니다.'));
    } finally {
      setLoadingMessage('');
    }
  };

  const openPlanModal = async () => {
    setModal('plans');
    await ensureResearchArtifacts('plan');
  };

  const openGuidelineModal = async () => {
    setModal('guidelines');
    await ensureResearchArtifacts('guideline');
  };

  useEffect(() => {
    if (modal === 'plans' && !selectedPlanId && planOptions[0]) {
      setSelectedPlanId(planOptions[0].id);
    }
  }, [modal, planOptions, selectedPlanId]);

  useEffect(() => {
    if (modal === 'guidelines' && !selectedGuidelineId && guidelineOptions[0]) {
      setSelectedGuidelineId(guidelineOptions[0].id);
    }
  }, [modal, guidelineOptions, selectedGuidelineId]);

  const generateQuestions = async () => {
    setModal(null);
    if (!form.interviewName.trim() || !form.goal.trim()) {
      setErrorMessage('인터뷰 이름과 목표를 입력한 뒤 질문을 생성해 주세요.');
      return;
    }
    setLoadingMessage('인터뷰 질문 생성 중입니다.');
    setErrorMessage('');
    try {
      const questionSet = await personaInterviewApi.generateQuestions({
        name: form.interviewName,
        goal: form.goal,
        productDescription: form.product,
        length: depth,
      });
      setDraft(interviewQuestionSetToDraft(questionSet));
    } catch (error) {
      setErrorMessage(formatInterviewError(error, '인터뷰 질문 생성에 실패했습니다.'));
    } finally {
      setLoadingMessage('');
    }
  };

  const changeDraftItem = (section, index, value, groupIndex = null) => {
    setDraft((current) => {
      if (!current) return current;
      const next = cloneValue(current);
      if (section === 'taskGroups') {
        next.taskGroups[groupIndex].questions[index] = value;
      } else {
        next[section][index] = value;
      }
      return next;
    });
  };

  const addDraftItem = (section, groupIndex = null) => {
    setDraft((current) => {
      if (!current) return current;
      const next = cloneValue(current);
      if (section === 'taskGroups') {
        next.taskGroups[groupIndex].questions.push('');
      } else {
        next[section].push('');
      }
      return next;
    });
  };

  const removeDraftItem = (section, index, groupIndex = null) => {
    setDraft((current) => {
      if (!current) return current;
      const next = cloneValue(current);
      if (section === 'taskGroups') {
        next.taskGroups[groupIndex].questions = next.taskGroups[groupIndex].questions.filter((_, itemIndex) => itemIndex !== index);
      } else {
        next[section] = next[section].filter((_, itemIndex) => itemIndex !== index);
      }
      return next;
    });
  };

  const addTaskGroup = () => {
    setDraft((current) => {
      if (!current) return current;
      const next = cloneValue(current);
      next.taskGroups.push({
        id: `task-${Date.now()}`,
        title: `영역${next.taskGroups.length + 1}`,
        questions: [''],
      });
      return next;
    });
  };

  const removeTaskGroup = (groupIndex) => {
    setDraft((current) => {
      if (!current) return current;
      const next = cloneValue(current);
      next.taskGroups = next.taskGroups.filter((_, index) => index !== groupIndex);
      return next;
    });
  };

  const changeTaskTitle = (groupIndex, value) => {
    setDraft((current) => {
      if (!current) return current;
      const next = cloneValue(current);
      next.taskGroups[groupIndex].title = value;
      return next;
    });
  };

  const togglePersona = (personaId) => {
    setSelectedPersonaIds((current) => {
      if (current.some((id) => sameId(id, personaId))) return current.filter((id) => !sameId(id, personaId));
      if (current.length >= INTERVIEW_PERSONA_SELECTION_LIMIT) return current;
      return [...current, personaId];
    });
  };

  const openInterviewResult = (loaded, personaIds) => {
    setInterview(loaded);
    setActivePersonaId(loaded?.results?.[0]?.personaId || loaded?.personaIds?.[0] || personaIds[0] || null);
    setShowResult(true);
    setErrorMessage('');
  };

  const runCreatedInterview = async (created, personaIds) => {
    try {
      const completed = await personaInterviewApi.runInterview(created.id, { personaIds });
      openInterviewResult(completed, personaIds);
      return true;
    } catch (runError) {
      const recovered = await recoverInterviewAfterRunError(created.id);
      if (recovered) {
        openInterviewResult(recovered, personaIds);
        if (String(recovered.status || '').toLowerCase() === 'completed_with_errors') {
          setToast({
            isOpen: true,
            type: 'success',
            message: '인터뷰가 생성되었지만 일부 퍼소나는 실패했습니다. 결과 화면에서 확인해 주세요.',
          });
        } else {
          setToast({
            isOpen: true,
            type: 'success',
            message: '인터뷰 생성이 완료되었습니다. 결과 화면으로 이동합니다.',
          });
        }
        return true;
      }

      navigate(INTERVIEW_LIST_PATH, {
        state: {
          toast: {
            type: 'error',
            message: formatInterviewError(runError, '인터뷰 실행에 실패했습니다. 테스트 목록에서 재시도해 주세요.'),
          },
        },
      });
      return false;
    }
  };

  const createInterview = async () => {
    if (!draft) {
      setErrorMessage('인터뷰 질문을 먼저 생성해 주세요.');
      return;
    }
    const personaIds = selectedPersonaIds.map((id) => Number(id)).filter((id) => Number.isFinite(id));
    if (personaIds.length === 0) {
      setErrorMessage('인터뷰에 참여할 퍼소나를 선택해 주세요.');
      return;
    }
    if (personaIds.length > INTERVIEW_PERSONA_SELECTION_LIMIT) {
      setErrorMessage(`인터뷰에 참여할 퍼소나는 최대 ${INTERVIEW_PERSONA_SELECTION_LIMIT}명까지 선택할 수 있습니다.`);
      return;
    }
    setCreatingInterview(true);
    setLoadingMessage('');
    setErrorMessage('');
    try {
      const created = await personaInterviewApi.createInterview({
        name: form.interviewName,
        goal: form.goal,
        productDescription: form.product,
        length: depth,
        questionSet: interviewDraftToQuestionSet(draft),
        personaIds,
      });
      await runCreatedInterview(created, personaIds);
    } catch (error) {
      setErrorMessage(formatInterviewError(error, '인터뷰 생성에 실패했습니다.'));
    } finally {
      setCreatingInterview(false);
      setLoadingMessage('');
    }
  };

  const handleCopyShareUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setToast({ isOpen: true, message: '링크가 복사되었습니다.', type: 'success' });
    } catch {
      setErrorMessage('공유 링크 복사에 실패했습니다.');
    }
  };

  const handleOpenShare = async () => {
    const targetId = interview?.id || interviewId;
    if (!targetId) return;
    try {
      const share = await personaResultShareApi.createShare('interview', targetId);
      setShareLink(share.shareUrl || '');
      setModal('share');
    } catch (error) {
      setErrorMessage(error.message || '공유 링크를 생성하지 못했습니다.');
    }
  };

  const handleExport = () => {
    const exportData = buildInterviewExportData(interview);
    downloadExportFile({
      filename: sanitizeCsvFilename(interview?.name || form.interviewName, 'interview-result'),
      format: exportFormat,
      ...exportData,
    });
    setModal(null);
  };

  const handleDeleteInterview = async () => {
    const targetId = interview?.id || interviewId;
    if (!targetId || deleting) return;
    setDeleting(true);
    setErrorMessage('');
    try {
      await personaInterviewApi.deleteInterview(targetId);
      navigate(INTERVIEW_LIST_PATH, {
        state: { toast: { message: '테스트를 삭제했습니다.', type: 'success' } },
      });
    } catch (error) {
      setErrorMessage(formatInterviewError(error, '인터뷰 삭제에 실패했습니다.'));
      setModal(null);
    } finally {
      setDeleting(false);
    }
  };

  if (creatingInterview) {
    return (
      <B2BLayout hideCollapseButton={false} defaultCollapsed={true} hidePageHeader={true}>
        <main className="persona-generation-progress">
          <InterviewGenerationProgress />
        </main>
      </B2BLayout>
    );
  }

  if (isShareView && loadingMessage) {
    return (
      <AIInterviewPageShell isShareView={isShareView} navigationGuard={handleNavigationGuard}>
        <div className="ai-interview-page">
          <main className="ai-interview-result-body">
            <section className="ai-interview-result-content">
              <PersonaState type="loading" title="불러오는 중" message="인터뷰 결과를 가져오고 있습니다." />
            </section>
          </main>
        </div>
      </AIInterviewPageShell>
    );
  }

  if (isShareView && !loadingMessage && !showResult) {
    return (
      <AIInterviewPageShell isShareView={isShareView} navigationGuard={handleNavigationGuard}>
        <div className="ai-interview-page">
          <main className="ai-interview-result-body">
            <section className="ai-interview-result-content">
              <PersonaState title="공유 링크를 찾을 수 없습니다" message={errorMessage || '공유 링크가 해제되었거나 결과가 삭제되었을 수 있습니다.'} />
            </section>
          </main>
        </div>
      </AIInterviewPageShell>
    );
  }

  return (
    <AIInterviewPageShell isShareView={isShareView} navigationGuard={handleNavigationGuard}>
      <div className="ai-interview-page">
        {showResult ? (
          <ResultView
            interview={interview}
            form={form}
            depth={depth}
            selectedPersonas={selectedPersonas}
            activePersonaId={activePersonaId}
            onActivePersonaChange={setActivePersonaId}
            onBack={handleBack}
            onOpenInsights={() => navigate(`/tests/interview/${interview?.id || interviewId}/insights`)}
            onOpenShare={handleOpenShare}
            onOpenDelete={() => setModal('delete')}
            onOpenExport={() => setModal('export')}
            shareMode={isShareView}
          />
        ) : (
          <>
            <InterviewHeader step={step} onBack={handleBack} />
            {step === 1 ? (
              <GoalStep
                form={form}
                draft={draft}
                depth={depth}
                canContinue={canContinueGoal}
                onFormChange={updateForm}
                onOpenPlans={openPlanModal}
                onOpenGuidelines={openGuidelineModal}
                onOpenDraftModal={() => setModal(draft ? 'regenerate' : 'draft')}
                onOpenRegenerate={() => setModal('regenerate')}
                onDraftChange={changeDraftItem}
                onAddDraftItem={addDraftItem}
                onRemoveDraftItem={removeDraftItem}
                onAddTaskGroup={addTaskGroup}
                onRemoveTaskGroup={removeTaskGroup}
                onTaskTitleChange={changeTaskTitle}
                onNext={() => setStep(2)}
              />
            ) : null}
            {step === 2 ? (
              <PersonaStep
                groups={personaGroups}
                loading={personasLoading}
                selectedPersonaIds={selectedPersonaIds}
                onToggle={togglePersona}
                onPrevious={() => requestPreviousStep(1)}
                onNext={() => setStep(3)}
              />
            ) : null}
            {step === 3 ? (
              <ReviewStep
                form={form}
                draft={draft}
                depth={depth}
                selectedPersonas={selectedPersonas}
                expanded={reviewExpanded}
                onToggleExpanded={() => setReviewExpanded((current) => !current)}
                onPrevious={() => setStep(2)}
                onCreate={createInterview}
              />
            ) : null}
          </>
        )}
        <ErrorNotice message={errorMessage} onClose={() => setErrorMessage('')} />
        {!isShareView && modal === 'plans' ? (
          <ResearchPlanImportModal
            isOpen
            plans={planOptions}
            loading={artifactsLoading}
            selectedPlanId={selectedPlanId}
            description={(
              <>
                Research에서 작성한 계획서를 선택해 주세요.
                <br />
                선택한 계획서의 목적과 제품 정보가 인터뷰 목표에 자동으로 입력됩니다.
              </>
            )}
            onSelect={setSelectedPlanId}
            onCancel={() => setModal(null)}
            onLoad={handleLoadPlan}
          />
        ) : null}
        {!isShareView && modal === 'guidelines' ? (
          <ResearchArtifactImportModal
            title="조사 가이드라인에서 질문 불러오기"
            description={(
              <>
                작성해 둔 조사 가이드라인을 선택해 주세요.
                <br />
                질문 영역이 인터뷰 질문 초안으로 복사되며, 이후 자유롭게 수정할 수 있습니다.
              </>
            )}
            items={guidelineOptions}
            loading={artifactsLoading}
            emptyMessage={(
              <>
                완료된 가이드라인이 없습니다.
                <br />
                Research에서 가이드라인을 먼저 만들어 주세요.
              </>
            )}
            selectedId={selectedGuidelineId}
            onSelect={setSelectedGuidelineId}
            onCancel={() => setModal(null)}
            onLoad={handleLoadGuideline}
            loadLabel="질문 불러오기"
          />
        ) : null}
        {!isShareView && modal === 'draft' ? (
          <QuestionDraftModal
            selectedDepth={depth}
            disabled={!form.interviewName.trim() || !form.goal.trim()}
            onSelect={setDepth}
            onCancel={() => setModal(null)}
            onGenerate={generateQuestions}
          />
        ) : null}
        {!isShareView && modal === 'regenerate' ? (
          <RegenerateModal
            onCancel={() => setModal(null)}
            onConfirm={() => {
              setDraft(null);
              generateQuestions();
            }}
          />
        ) : null}
        {!isShareView && modal === 'share' ? (
          <ResultModalFrame
            title="공유하기"
            description="아래 링크를 통해 접속하면 테스트 결과를 볼 수 있습니다."
            footer={<button type="button" onClick={() => setModal(null)}>확인</button>}
          >
            <label className="ai-interview-share-field">
              <span>공유링크</span>
              <div>
                <input value={shareUrl} readOnly />
                <button type="button" onClick={handleCopyShareUrl} aria-label="공유 링크 복사">
                  <Copy size={20} />
                </button>
              </div>
            </label>
            <small>{`${formatSharedAt(interview?.createdAt)}에 공유됨`}</small>
          </ResultModalFrame>
        ) : null}
        {!isShareView && modal === 'export' ? (
          <ResultModalFrame
            title="내보내기"
            description="내보낼 파일 형식을 선택해주세요."
            footer={(
              <>
                <button type="button" onClick={() => setModal(null)}>취소하기</button>
                <button type="button" onClick={handleExport}>내보내기</button>
              </>
            )}
          >
            <div className="ui-result-export-options">
              {EXPORT_OPTIONS.map((option) => (
                <label key={option.value} className={exportFormat === option.value ? 'selected' : ''}>
                  <input
                    type="radio"
                    name="interviewExportFormat"
                    value={option.value}
                    checked={exportFormat === option.value}
                    onChange={(event) => setExportFormat(event.target.value)}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </ResultModalFrame>
        ) : null}
        {!isShareView && modal === 'delete' ? (
          <DeleteInterviewModal
            deleting={deleting}
            onCancel={() => setModal(null)}
            onConfirm={handleDeleteInterview}
          />
        ) : null}
        {!isShareView ? (
          <PageLeaveConfirmModal
            isOpen={Boolean(pendingExit)}
            message="작성 중인 인터뷰 정보가 저장되지 않습니다."
            confirmText={pendingExit?.type === 'step' ? '이전으로 이동' : '나가기'}
            onCancel={handleCancelPendingExit}
            onConfirm={handleConfirmPendingExit}
          />
        ) : null}
        <Toast
          isOpen={toast.isOpen}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast((current) => ({ ...current, isOpen: false }))}
        />
        {loadingMessage ? <LoadingOverlay message={loadingMessage} /> : null}
      </div>
    </AIInterviewPageShell>
  );
}

export default B2BAIInterviewPage;
